/**
 * In-memory filesystem shape used by the interactive terminal.
 * Mirrors the public site's navigation: a `~` root with a `blog/`
 * directory, a `projects/` directory, and a few top-level files
 * (`about.md`, `contact.sh`). Keys at every level are flat strings
 * (so `fs["~/blog"]` works the same as `fs["~"].children.blog`),
 * which lets the shell's `cd`/`ls`/`cat` resolve any path with a
 * single lookup.
 *
 * The shape and the helpers below are intentionally separate from
 * `lib/fake-fs.ts` (which **builds** the fs from real posts/
 * projects on disk) — `terminal-fs.ts` is the pure shape + path
 * math the shell uses at runtime.
 */

export type FsEntry =
  | string
  | { type: "dir"; children: Record<string, FsEntry> };

export type Fs = Record<string, FsEntry>;

export function isDir(
  entry: FsEntry | undefined
): entry is { type: "dir"; children: Record<string, FsEntry> } {
  return !!entry && typeof entry === "object" && entry.type === "dir";
}

export function isFile(entry: FsEntry | undefined): entry is string {
  return typeof entry === "string";
}

/** The real path that `~` stands for. Only home is visible; anything
 *  outside it exists "on the box" but is permission-denied. */
export const HOME = "/home/emily";

/** Absolute path of an fs key (`~/blog` → `/home/emily/blog`). What
 *  `pwd` prints. */
export function toAbsolute(key: string): string {
  return key === "~" ? HOME : HOME + key.slice(1);
}

/** Resolve a path typed by the user against cwd (an fs key) and
 *  return the fs key it points to (`~` or `~/...`). Returns `null`
 *  when the path leaves home (`/`, `/etc/passwd`, `cd ..` from `~`)
 *  so callers can report "permission denied". */
export function resolve(p: string, cwd: string): string | null {
  let abs: string;
  if (p === "" || p === "~") abs = HOME;
  else if (p.startsWith("~/")) abs = HOME + p.slice(1);
  else if (p.startsWith("/")) abs = p;
  else abs = toAbsolute(cwd) + "/" + p;

  const parts: string[] = [];
  for (const seg of abs.split("/")) {
    if (seg === "" || seg === ".") continue;
    if (seg === "..") parts.pop();
    else parts.push(seg);
  }
  const normalized = "/" + parts.join("/");
  if (normalized === HOME) return "~";
  if (normalized.startsWith(HOME + "/")) return "~" + normalized.slice(HOME.length);
  return null;
}

/** What `uname` prints (and `uname -a` prints without the
 *  hidden kernel config). Hardcoded here so the same string is
 *  shared by the shell and any future server-side reference. */
export const UNAME_STRING =
  "Linux byemily.org 6.12.31-arch1-1 #1 SMP PREEMPT_DYNAMIC x86_64";
